import { Link } from "react-router-dom";
import {
  IdCard,
  FileText,
  Car,
  ShieldCheck,
  ClipboardList,
  Handshake,
  ShieldAlert,
  ListChecks,
  Ban,
  CheckCircle2,
} from "lucide-react";
import useSeo from "../hooks/useSeo";

const SECTIONS = [
  {
    id: "personales",
    n: "1",
    icon: IdCard,
    title: "Requisitos personales del anfitrión",
    body: (
      <>
        <p>El anfitrión deberá:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Ser mayor de 21 años.</li>
          <li>Contar con identificación oficial vigente.</li>
          <li>Proporcionar una fotografía clara de perfil.</li>
          <li>Tener número de teléfono y correo electrónico verificados.</li>
          <li>
            Aceptar los{" "}
            <Link to="/terminos" className="text-terracotta font-semibold hover:underline">
              Términos y Condiciones
            </Link>
            ,{" "}
            <Link to="/privacidad" className="text-terracotta font-semibold hover:underline">
              Aviso de Privacidad
            </Link>{" "}
            y Políticas de RivoNes.
          </li>
          <li>
            No tener antecedentes relacionados con fraude, robo de vehículos o actividades
            que representen riesgo para la plataforma, usuarios o terceros.
          </li>
          <li>Mantener una comunicación respetuosa, clara y profesional con los usuarios.</li>
        </ul>
      </>
    ),
  },
  {
    id: "docs-anfitrion",
    n: "2",
    icon: FileText,
    title: "Documentos requeridos",
    body: (
      <>
        <p>El anfitrión deberá subir a la plataforma:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Identificación oficial vigente, como INE, pasaporte o residencia.</li>
          <li>Licencia de conducir vigente.</li>
          <li>Comprobante de domicilio reciente.</li>
          <li>Fotografía tipo selfie para validación de identidad.</li>
          <li>Selfie sosteniendo su identificación oficial.</li>
          <li>Datos bancarios o método de pago autorizado para recibir ganancias.</li>
          <li>
            En caso de persona moral, documentación legal de la empresa y datos del
            representante legal.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "requisitos-vehiculo",
    n: "3",
    icon: Car,
    title: "Requisitos del vehículo",
    body: (
      <>
        <p>El vehículo deberá:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Estar en buenas condiciones mecánicas, estéticas y de seguridad.</li>
          <li>Contar con placas vigentes.</li>
          <li>Tener tarjeta de circulación vigente.</li>
          <li>Contar con seguro vehicular vigente en México.</li>
          <li>Estar libre de reporte de robo.</li>
          <li>No presentar fallas mecánicas graves.</li>
          <li>
            Tener llantas, frenos, luces, cinturones de seguridad, espejos y limpiaparabrisas
            en buen estado.
          </li>
          <li>Estar limpio y listo para entregarse al usuario.</li>
          <li>Tener aire acondicionado funcional, cuando se anuncie como parte del vehículo.</li>
        </ul>
      </>
    ),
  },
  {
    id: "docs-vehiculo",
    n: "4",
    icon: ClipboardList,
    title: "Documentos requeridos del vehículo",
    body: (
      <>
        <p>El anfitrión deberá subir:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Tarjeta de circulación vigente.</li>
          <li>Póliza de seguro vigente.</li>
          <li>
            Comprobante de propiedad o documento que acredite autorización para rentar el
            vehículo.
          </li>
          <li>Fotografías claras del exterior, interior, placas, tablero y odómetro.</li>
          <li>Fotografía del número de serie/VIN cuando sea requerido.</li>
          <li>Comprobante de verificación vehicular, si aplica según la ciudad o estado.</li>
        </ul>
      </>
    ),
  },
  {
    id: "publicacion",
    n: "5",
    icon: ListChecks,
    title: "Reglas de publicación",
    body: (
      <>
        <p>El anfitrión deberá publicar información real y actualizada del vehículo, incluyendo:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Marca, modelo, año y versión.</li>
          <li>Tipo de transmisión.</li>
          <li>Tipo de combustible.</li>
          <li>Número de pasajeros.</li>
          <li>Kilometraje disponible.</li>
          <li>Precio por día.</li>
          <li>Reglas especiales del vehículo.</li>
          <li>Ubicación aproximada de entrega.</li>
          <li>Disponibilidad real del vehículo.</li>
        </ul>
        <p className="mt-2">
          No se permite publicar vehículos con información falsa, fotografías engañosas,
          precios incorrectos o condiciones diferentes a las ofrecidas.
        </p>
      </>
    ),
  },
  {
    id: "responsabilidades",
    n: "6",
    icon: Handshake,
    title: "Responsabilidades del anfitrión",
    body: (
      <>
        <p>El anfitrión será responsable de:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Entregar el vehículo limpio, seguro y en condiciones de uso.</li>
          <li>Verificar el estado del vehículo antes y después de cada renta.</li>
          <li>Tomar fotografías del vehículo antes de la entrega y después de la devolución.</li>
          <li>Reportar daños, retrasos o incidentes a RivoNes de manera inmediata.</li>
          <li>Respetar los horarios acordados de entrega y devolución.</li>
          <li>No cancelar reservaciones confirmadas sin causa justificada.</li>
          <li>No solicitar pagos fuera de la plataforma.</li>
          <li>
            No discriminar a usuarios por nacionalidad, género, apariencia, idioma, religión,
            edad, orientación sexual o cualquier otra condición.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "seguro",
    n: "7",
    icon: ShieldCheck,
    title: "Seguro y responsabilidad",
    body: (
      <>
        <p>Todo vehículo publicado en RivoNes deberá contar con seguro vigente en México.</p>
        <p className="mt-2">El anfitrión entiende que:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>El seguro personal del vehículo puede tener restricciones para uso de renta o uso comercial.</li>
          <li>RivoNes podrá solicitar información adicional sobre la cobertura.</li>
          <li>El anfitrión deberá informar cualquier limitación de su póliza.</li>
          <li>
            En caso de accidente, daño, robo o siniestro, el anfitrión deberá colaborar con
            RivoNes, el usuario, la aseguradora y las autoridades correspondientes.
          </li>
        </ul>
        <p className="mt-2">
          RivoNes podrá requerir coberturas adicionales, seguro por día o protección especial
          antes de permitir que el vehículo sea rentado.
        </p>
      </>
    ),
  },
  {
    id: "verificacion",
    n: "8",
    icon: ShieldAlert,
    title: "Verificación y aprobación",
    body: (
      <>
        <p>
          RivoNes podrá revisar, aprobar, rechazar, pausar o eliminar cuentas de anfitriones
          o publicaciones de vehículos cuando:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>La información sea falsa o incompleta.</li>
          <li>Los documentos estén vencidos.</li>
          <li>El vehículo no cumpla con los estándares mínimos.</li>
          <li>Exista comportamiento sospechoso.</li>
          <li>Se reciban quejas repetidas de usuarios.</li>
          <li>El anfitrión incumpla las políticas de la plataforma.</li>
        </ul>
      </>
    ),
  },
  {
    id: "suspension",
    n: "9",
    icon: Ban,
    title: "Causas de suspensión",
    body: (
      <>
        <p>El anfitrión podrá ser suspendido o eliminado de RivoNes si:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Entrega un vehículo diferente al publicado.</li>
          <li>Cobra dinero fuera de la plataforma.</li>
          <li>Presenta documentos falsos.</li>
          <li>No entrega el vehículo después de aceptar una reserva.</li>
          <li>Tiene conducta agresiva, fraudulenta o poco profesional.</li>
          <li>Oculta daños, fallas mecánicas o información importante.</li>
          <li>Intenta contactar al usuario para evitar las comisiones de RivoNes.</li>
          <li>No coopera en caso de accidente, reclamo o investigación.</li>
        </ul>
      </>
    ),
  },
  {
    id: "declaracion",
    n: "10",
    icon: CheckCircle2,
    title: "Declaración del anfitrión",
    body: (
      <>
        <p>
          Al registrarse como anfitrión en RivoNes, el usuario declara que toda la
          información proporcionada es verdadera, actual y verificable. También acepta
          cumplir con las políticas de seguridad, operación, pagos, cancelaciones, privacidad
          y uso de la plataforma.
        </p>
        <div className="mt-4 p-5 rounded-2xl border border-emerald-200 bg-emerald-50 text-sm not-prose">
          <p className="font-semibold text-emerald-900 mb-1">Confirmación al registrarse</p>
          <p className="text-emerald-900/90">
            "Confirmo que cumplo con los requisitos para ser anfitrión en RivoNes, que la
            información y documentos proporcionados son verdaderos, y acepto las políticas,
            términos y condiciones de la plataforma."
          </p>
        </div>
      </>
    ),
  },
];

export default function RequisitosHostPage() {
  useSeo({
    title: "Requisitos para Anfitriones · Rivones",
    description:
      "Conoce los requisitos personales, documentación, requisitos del vehículo, seguro y responsabilidades para ser anfitrión en Rivones.",
    keywords:
      "requisitos anfitrión rivones, host requirements, publicar auto rivones, seguro vehículo rivones",
    canonical: "https://rivones.com/requisitos-host",
  });

  return (
    <div className="bg-sand min-h-screen" data-testid="requisitos-host-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-neutral-50 to-sand border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <p className="text-sm font-semibold tracking-[0.2em] text-terracotta uppercase">
            Para Anfitriones
          </p>
          <h1
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#111111] tracking-tight mt-3"
            data-testid="requisitos-host-h1"
          >
            Requisitos para Anfitriones
          </h1>
          <p className="text-base sm:text-lg text-neutral-600 mt-4 leading-relaxed">
            Para poder publicar un vehículo y rentarlo dentro de RivoNes, todos los
            anfitriones deberán cumplir con los siguientes requisitos.
          </p>
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 mt-6 font-semibold">
            Última actualización · Febrero 2026
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* TOC */}
        <nav
          aria-label="Índice"
          className="bg-white border border-neutral-200 rounded-2xl p-5 mb-10"
          data-testid="requisitos-host-toc"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">
            Índice
          </p>
          <ol className="space-y-1.5 text-sm">
            {SECTIONS.map(({ id, n, title }) => (
              <li key={id}>
                <a
                  href={`#${id}`}
                  className="text-neutral-700 hover:text-terracotta transition-colors flex gap-2"
                >
                  <span className="text-neutral-400 tabular-nums">{n}.</span>
                  <span>{title}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="space-y-12">
          {SECTIONS.map(({ id, n, icon: Icon, title, body }) => (
            <article
              key={id}
              id={id}
              data-testid={`requisitos-host-section-${id}`}
              className="scroll-mt-24"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex w-11 h-11 rounded-xl bg-neutral-100 items-center justify-center">
                  <Icon className="w-5 h-5 text-[#111111]" strokeWidth={2} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-neutral-400 font-semibold">
                    Sección {n}
                  </p>
                  <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#111111] leading-tight">
                    {title}
                  </h2>
                </div>
              </div>
              <div className="max-w-none text-neutral-700 leading-relaxed [&_p]:my-3 [&_strong]:text-[#111111] [&_strong]:font-semibold [&_ul]:my-3 [&_ol]:my-3">
                {body}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 p-5 rounded-2xl bg-neutral-100 border border-neutral-200 text-sm text-neutral-600">
          ¿Listo para empezar?{" "}
          <Link to="/become-host" className="text-terracotta font-semibold hover:underline">
            Registra tu vehículo
          </Link>{" "}
          o lee también las{" "}
          <Link to="/preguntas-frecuentes" className="text-terracotta font-semibold hover:underline">
            Preguntas Frecuentes
          </Link>
          .
        </div>
      </section>
    </div>
  );
}
